(ns lupapalvelu.pate.verdict-util
  "Utility CLJC functions for verdicts and verdict templates"
  (:require #?(:clj  [sade.env :as env]
               :cljs [lupapalvelu.ui.common :as common])
            [sade.strings :as ss]
            [sade.util :as util]))

(defn loc-item
  "Returns the corresponding item from the verdict template settings for the given
  `value`. The value is interpreted either id or code field. Returns nil if no match is
  found."
  [verdict reference-kw value]
  (let [refs (get-in verdict [:references reference-kw])]
    (when value
      (or (util/find-by-id value refs)
          (util/find-by-key :code value refs)))))

(defn loc-item-text
  "Returns the corresponding localisation from the verdict template settings for the given
  `value`. The value is interpreted either id or code field. Returns `value` if no
  localisation was found."
  [verdict reference-kw value lang]
  (get (loc-item verdict reference-kw value)
       (keyword lang)
       value))

(defn blocks-template?
  "Returns true if the template blocker (reason not to use the verdict template to create a new verdict)
  should actually block the user or just warn them against using the template"
  [blocker]
  (and blocker
       (util/not=as-kw blocker :kuulutus-date-missing)))

(defn category-supports-giver-names?
  [category]
  (util/includes-as-kw? [:r :p] category))

(defn verdict-supports-giver-names?
  "Only :r and :p categoried verdicts support verdict-giver names"
  [verdict]
  (category-supports-giver-names? (:category verdict)))

(defn decision-documents-enabled?
  "Whether externally produced board decision documents are available. On the
  frontend this is already called when the verdict schema namespaces load, hence
  the guard against `js/features` not being defined yet."
  []
  #?(:clj  (env/feature? :external-verdict-documents)
     :cljs (and (exists? js/features)
                (common/feature? :external-verdict-documents))))

(defn category-supports-decision-documents?
  "Whether `category` supports the externally produced decision-document
  workflow. Only the board-verdict categories do, and only while the feature is
  enabled."
  [category]
  (and (decision-documents-enabled?)
       (category-supports-giver-names? category)))

(defn viranhaltija-verdict?
  [verdict]
  (util/=as-kw :viranhaltija (-> verdict :template :giver)))

(def board-workflow-types
  "Giver values whose verdicts go through the board proposal workflow.

  Both denote the same giver -- the lautakunta -- and differ only in which board
  document is produced. `giver` flattens the giver and that document role into
  one field, so that the template editor needs only a single select."
  [:lautakunta :valmisteluasiakirja])

(defn board-workflow-type?
  "True if `giver` uses the board proposal workflow."
  [giver]
  (util/includes-as-kw? board-workflow-types giver))

(defn resolve-verdict-document-source
  "Effective verdict-document-source (\"lupapiste-generated\" or \"external\") for
  the given published template data.

  The stored value cannot be trusted on its own: the template editor only *hides*
  the selector when the giver type is switched to viranhaltija, so a template that
  was briefly a board template can still carry a stale \"external\" value. A
  viranhaltija verdict has no UI for attaching an external decision, so honouring
  that value would make the verdict impossible to publish."
  [{:keys [giver verdict-document-source]}]
  (if (util/=as-kw giver :viranhaltija)
    "lupapiste-generated"
    (or (not-empty verdict-document-source)
        (if (util/=as-kw giver :valmisteluasiakirja)
          "external"
          "lupapiste-generated"))))

(defn external-decision-attachment
  "Returns the externally attached decision attachment for the given verdict from
  `attachments`, or nil. The attachment must target the verdict (the upload flow
  sets target, not source, for user-uploaded verdict attachments), be of type
  paatoksenteko.paatos and have at least one version. A versionless attachment is
  ignored, since it can never satisfy the publishing precheck."
  [attachments verdict-id]
  (util/find-first (fn [att]
                     (and (= (get-in att [:target :type]) "verdict")
                          (= (get-in att [:target :id]) verdict-id)
                          (= (get-in att [:type :type-group]) "paatoksenteko")
                          (= (get-in att [:type :type-id]) "paatos")
                          (seq (:versions att))))
                   attachments))

(defn valid-verdict-giver?
  "Checks if a verdict giver is valid for the given `giver-type`.
   For viranhaltija: must have both first-name AND last-name
   For lautakunta and valmisteluasiakirja: must have fi, sv, and en values
   Other/unknown giver types always pass validation."
  [giver-type item]
  (cond
    (util/=as-kw giver-type :viranhaltija)
    (and (ss/not-blank? (:first-name item))
         (ss/not-blank? (:last-name item)))

    (board-workflow-type? giver-type)
    (and (ss/not-blank? (:fi item))
         (ss/not-blank? (:sv item))
         (ss/not-blank? (:en item)))

    :else true))
